import * as React from "react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {isTouchDevice} from "../../helpers/deviceInformation";
import {cdnDomain} from "../../helpers/cnxDomains";
import {getShopId} from "../lib/getShopId";
import {Tracking} from "../../tracking/tracking";
import window from "../lib/WindowExtended";
import styles from "./GiftBlock.module.scss";

/**
 * Option to print gifts with delivery delay as PDF gift card ("#ProductGiftBlock")
 */

type GiftBlockProps = {
  productTitle: string;
  productImage: string;
};
const GiftBlock: React.FC<GiftBlockProps> = ({productTitle, productImage}) => {
  const {t} = useTranslation();
  const [showInfo, setShowInfo] = useState<boolean>(false);

  const deGiftCardName = "Connox-Geschenkkarte-zum-Ausdrucken-DE.jpg";
  const enGiftCardName = "Printable-Connox-Gift-Card-EN.jpg";
  const frGiftCardName = "Carte-cadeau-Connox-a-imprimer-FR.jpg";
  const daGiftCardName = "Connox-gavekort-til-print-DA.jpg";
  const nlGiftCardName = "Connox-cadeaubon-om-te-printen-NL.jpg";

  const giftCardFileName: {[key: number]: string} = {
    100001: deGiftCardName,
    100009: deGiftCardName,
    100017: deGiftCardName,
    100027: frGiftCardName,
    100008: frGiftCardName,
    100005: enGiftCardName,
    100020: enGiftCardName,
    100022: daGiftCardName,
    100029: nlGiftCardName,
  };

  const imgRegExp = productImage?.match("/media/.+.jpg");
  const productImgUrl = imgRegExp ? cdnDomain[getShopId()] + imgRegExp[0] : productImage;
  const baseImgUrl = cdnDomain[getShopId()] + "/media/Gutscheine/" + giftCardFileName[getShopId()];

  const printFunction = () => {
    const printContent = document.getElementById("GiftBlockPrintArea") as HTMLDivElement;
    const title = "connox-" + t("product.giftBlock.printTitle");
    const lang = document.body.dataset.lang ?? "en";

    if (!printContent || !baseImgUrl || !productImgUrl) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="${lang}">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>${title}</title>
          <style>
            body {
              margin: 0;
              font-family: sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            i = 0;
            function onloadfunction() {
              i++;
              if (i === 2) window.print();
            }
            // Only open the print dialog after both images have been fully loaded
            document.body.querySelector(".background-print").onload = onloadfunction;
            document.body.querySelector(".product-print").onload = onloadfunction;
          </script>
        </body>
      </html>
    `;

    // Create a blob url and open it in a new tab
    const blob = new Blob([htmlContent], {type: "text/html"});
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");

    Tracking.sendEvent("GiftBlock.PrintLink.Clicked");
  };

  const closeInfo = () => {
    setShowInfo(false);
  };

  const GiftCardInfoText: React.FC = () => {
    return (
      <div className={styles.info}>
        <div className={styles.infoHead}>
          <p>{t("product.giftBlock.infoTitle")}</p>
          {isTouchDevice() && (
            <button className={styles.infoClose} onClick={closeInfo}>
              <i className="icon icon-size--14 icon-close" />
            </button>
          )}
        </div>
        <p>{t("product.giftBlock.infoText")}</p>
      </div>
    );
  };

  return (
    <>
      <span className={styles.link}>
        <i className="icon icon-size--24 icon-gift" />
        <button className={`${styles.linkText} link`} onClick={printFunction}>
          {t("product.giftBlock.linkTitle")}
        </button>
        <button
          className={styles.linkInfo}
          onClick={() => setShowInfo(!showInfo)}
          onMouseEnter={() => setShowInfo(true)}
          onMouseLeave={closeInfo}
        >
          <i className="icon icon-size--18 icon-info--yellow" />
        </button>
        {showInfo && <GiftCardInfoText />}
      </span>
      <div className={styles.printArea} id="GiftBlockPrintArea" style={{display: "none"}}>
        <div style={{position: "relative", width: "970px", height: "690px"}}>
          <img className="background-print" src={baseImgUrl} alt={t("product.giftBlock.printTitle")} height={"100%"} />
          <img
            className="product-print"
            style={{
              position: "absolute",
              top: "135px",
              right: "90px",
              mixBlendMode: "multiply",
            }}
            src={productImgUrl}
            alt={productTitle}
            width={"340"}
          />
          <p
            style={{
              position: "absolute",
              bottom: "22px",
              left: "36px",
              margin: "0",
              fontSize: "26px",
              fontFamily: "sans-serif",
              maxWidth: "740px",
              minHeight: "48px",
              maxHeight: "65px",
              overflow: "hidden",
            }}
          >
            {productTitle}
          </p>
        </div>
      </div>
    </>
  );
};

export default GiftBlock;
